import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, queryString, type LabelLookupResult, type LabelProduct } from '../api/client.js';
import { DymoConnectNote, LabelPrintNote } from '../components/LabelPrinting.js';
import { ProductThumb } from '../components/ProductThumb.js';
import { Alert, Button, Card, Input, PageHeader, TableScroll } from '../components/ui.js';
import { LABEL_SETTINGS_PATH } from '../lib/nav.js';
import { matchedByText, productName, taxonomyLine } from '../labels/describe.js';
import {
  DEFAULT_DESTINATION_ID,
  destinationById,
  type LabelDestinationId,
} from '../labels/destinations.js';
import { refreshDymoStatus } from '../labels/dymo-browser.js';
import { DymoPrintError } from '../labels/dymo-status.js';
import { labelsPerPage } from '../labels/layout.js';
import { printLabels, type PdfDestinationId, type PrintOutcome } from '../labels/print.js';
import {
  MAX_QTY,
  MIN_QTY,
  QUEUE_STORAGE_KEY,
  addProduct,
  parseQuantity,
  parseQueue,
  queueTotals,
  removeRow,
  serializeQueue,
  setQuantity,
  type QueueRow,
} from '../labels/queue.js';
import { useDymoStatus } from '../labels/useDymoStatus.js';
import { dymoAvailability, useLabelDestination } from '../labels/useLabelDestination.js';

/**
 * Labeling (`/products/labeling`): scan products, print their FNSKU labels.
 *
 * One input box takes a manufacturer barcode off a scanner, or a pasted ASIN,
 * FNSKU, or seller SKU. Enter looks it up and adds the product as a row under
 * the last one; the box keeps the focus, so the next scan is the next row. Each
 * row shows the picture and enough words to confirm it is the product in hand,
 * a label quantity, and a remove button. "Print all labels" sends the whole
 * list as one job to this browser's destination — a PDF for the browser to
 * print, or straight to the DYMO label printer with no dialog; "Cancel"
 * discards the list.
 *
 * When this browser's destination is the DYMO printer, the page asks DYMO
 * Connect whether one is there. If not, it says so and prints to the default
 * (sheet labels) instead; if a print to it fails, the list is untouched and the
 * PDF is one click away.
 *
 * The list lives in the page and in sessionStorage, so a refresh keeps it and
 * closing the tab ends it. It is kept after a print (a jam means printing
 * again) until "Clear list". Nothing here changes the server. See
 * docs/labels.md.
 */

function readStoredQueue(): QueueRow[] {
  try {
    return parseQueue(sessionStorage.getItem(QUEUE_STORAGE_KEY));
  } catch {
    return [];
  }
}

/** A barcode that named several products, waiting for the user to pick one. */
interface PendingChoice {
  query: string;
  products: LabelProduct[];
}

interface Notice {
  tone: 'error' | 'info';
  text: string;
}

/** A finished print: the outcome and, for a PDF, the object URL its document is opened from. */
type Printed = PrintOutcome & {
  /** The PDF's object URL; null for a print sent straight to the printer. */
  url: string | null;
  /** False when the browser refused the new tab, so the page must say to open it by hand. */
  opened: boolean;
  skippedRows: number;
};

/** A print that failed, and whether it was the DYMO printer that failed (the PDF is then offered). */
interface PrintFailure {
  message: string;
  dymo: boolean;
}

/** The two ways to print the list as a PDF when the DYMO printer cannot take it (yet). */
function PdfInsteadLinks({ onPrint }: { onPrint: (id: PdfDestinationId) => void }) {
  return (
    <p className="flex flex-wrap items-center gap-4">
      <button
        type="button"
        onClick={() => onPrint('sheet-30up')}
        className="font-medium underline underline-offset-2"
      >
        PDF for sheet labels
      </button>
      <button
        type="button"
        onClick={() => onPrint('roll-30336')}
        className="font-medium underline underline-offset-2"
      >
        PDF for the 30336 roll
      </button>
    </p>
  );
}

export function LabelingPage() {
  const [rows, setRows] = useState<QueueRow[]>(readStoredQueue);
  // The lookups run one after another and each needs the list as it is NOW, not
  // as it was when its request began, so the list is mirrored in a ref.
  const rowsRef = useRef(rows);
  const [entry, setEntry] = useState('');
  const [notice, setNotice] = useState<Notice | null>(null);
  const [choice, setChoice] = useState<PendingChoice | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [pending, setPending] = useState(0);
  const [startAtText, setStartAtText] = useState('1');
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<PrintFailure | null>(null);
  const [printed, setPrinted] = useState<Printed | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const selectEntry = useRef(false);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const [chosenId] = useLabelDestination();
  // The DYMO printer is looked for only when this browser chose it: the look can
  // make the browser ask the user a question, and a browser that prints PDFs
  // should never be asked it.
  const dymoChosen = destinationById(chosenId).kind === 'dymo';
  const dymo = useDymoStatus(dymoChosen);
  const dymoAvailable = dymoAvailability(dymo);
  /** The first check has not answered yet: the destination is not known, so nothing prints. */
  const dymoPending = dymoChosen && dymo.status === null;
  /** The DYMO printer was chosen but is not there: the default is used, and the page says so. */
  const dymoFellBack = dymoChosen && !dymoPending && !dymoAvailable.available;
  /**
   * The first check is held up by the browser's permission question. It carries
   * on (Print stays "Checking the printer…"), but nobody is made to wait for it:
   * the page says what the browser is waiting for and offers the PDF meanwhile.
   */
  const dymoWaitingForPermission = dymoPending && dymo.waitingForPermission;
  const destinationId: LabelDestinationId = dymoFellBack ? DEFAULT_DESTINATION_ID : chosenId;
  const destination = destinationById(destinationId);

  function commit(next: QueueRow[]) {
    rowsRef.current = next;
    setRows(next);
    // A finished print speaks for the list it printed; once the list changes,
    // its counts would be wrong, so the result is put away.
    setPrinted(null);
  }

  useEffect(() => {
    try {
      sessionStorage.setItem(QUEUE_STORAGE_KEY, serializeQueue(rows));
    } catch {
      // The list simply will not survive a refresh; the page still works.
    }
  }, [rows]);

  // The highlight is a flash, not a state: it fades so the next scan of the same
  // product can flash again.
  useEffect(() => {
    if (highlight === null) return;
    const row = document.querySelector<HTMLElement>(`[data-row-id="${CSS.escape(highlight)}"]`);
    row?.scrollIntoView({ block: 'nearest' });
    const timer = window.setTimeout(() => setHighlight(null), 1600);
    return () => window.clearTimeout(timer);
  }, [highlight]);

  // A failed entry is put back in the box selected, so the next scan replaces it.
  useEffect(() => {
    if (!selectEntry.current) return;
    selectEntry.current = false;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [entry]);

  // The document a print made is released when it is replaced or the page closes.
  const printedUrl = printed?.url ?? null;
  useEffect(() => {
    if (printedUrl === null) return;
    return () => URL.revokeObjectURL(printedUrl);
  }, [printedUrl]);

  function focusInput() {
    inputRef.current?.focus();
  }

  function add(product: LabelProduct) {
    const result = addProduct(rowsRef.current, product);
    commit(result.rows);
    setHighlight(result.rowId);
    const qty = result.rows.find((r) => r.id === result.rowId)?.qty ?? MIN_QTY;
    setAnnouncement(
      result.incremented
        ? `${productName(product)}: quantity is now ${qty}.`
        : `Added ${productName(product)}.`,
    );
  }

  /** Puts a failed entry back in the box, selected — unless a new scan is already there. */
  function restoreEntry(text: string) {
    if ((inputRef.current?.value ?? '') !== '') return;
    selectEntry.current = true;
    setEntry(text);
  }

  async function lookUp(text: string): Promise<void> {
    let result: LabelLookupResult;
    try {
      result = await api.get<LabelLookupResult>(`/api/labels/lookup${queryString({ q: text })}`);
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Lookup failed.' });
      restoreEntry(text);
      return;
    }
    if (result.status === 'not_found') {
      const message = `No product found for “${result.query}”. Nothing was added.`;
      setNotice({ tone: 'error', text: message });
      setAnnouncement(message);
      restoreEntry(text);
      return;
    }
    setNotice(null);
    if (result.status === 'choose') {
      setChoice({ query: result.query, products: result.products });
      setAnnouncement(`${result.products.length} products match. Choose one.`);
      return;
    }
    const [product] = result.products;
    if (product) add(product);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const text = entry.trim();
    if (text === '') return;
    // The box is emptied at once so the next scan lands in an empty box, and the
    // lookups are chained so two quick scans are added in the order scanned.
    setEntry('');
    setChoice(null);
    setPending((n) => n + 1);
    chain.current = chain.current.then(() => lookUp(text)).finally(() => setPending((n) => n - 1));
    focusInput();
  }

  function choose(product: LabelProduct) {
    setChoice(null);
    add(product);
    focusInput();
  }

  function onCancel() {
    if (rows.length === 0) return;
    const count =
      rows.length === 1 ? 'the 1 scanned product' : `all ${rows.length} scanned products`;
    if (!window.confirm(`Discard ${count}?`)) return;
    clearList();
  }

  function clearList() {
    commit([]);
    setChoice(null);
    setNotice(null);
    setPrinted(null);
    setPrintError(null);
    setAnnouncement('The list is empty.');
    focusInput();
  }

  const totals = queueTotals(rows);
  const perPage = labelsPerPage(destination.stock);
  const startAt = destination.supportsStartAt ? parseStartAt(startAtText, perPage) : 1;
  const canPrint = totals.labels > 0 && startAt !== null && !printing && !dymoPending;

  /**
   * Prints the list. `override` is the PDF the user asked for after the DYMO
   * printer failed; without it the job goes to the destination on screen.
   */
  async function onPrint(override?: LabelDestinationId) {
    if (totals.labels === 0 || printing) return;
    if (override === undefined && (!canPrint || startAt === null)) return;
    const target = override ?? destinationId;
    const toPdf = destinationById(target).kind === 'pdf';
    // The tab is opened inside the click, before any waiting, or the browser
    // treats it as a pop-up and blocks it. It shows the document once it exists.
    // A print sent straight to the printer opens nothing.
    const tab = toPdf ? window.open('', '_blank') : null;
    if (tab) {
      tab.document.title = 'FNSKU labels';
      tab.document.body.textContent = 'Preparing labels…';
    }
    setPrinting(true);
    setPrintError(null);
    try {
      const outcome = await printLabels(rowsRef.current, target, { startAt: startAt ?? 1 });
      const url = outcome.kind === 'pdf' ? URL.createObjectURL(outcome.blob) : null;
      if (tab && url) tab.location.replace(url);
      setPrinted({ ...outcome, url, opened: tab !== null, skippedRows: totals.skippedRows });
    } catch (error) {
      tab?.close();
      setPrinted(null);
      setPrintError({
        message: error instanceof Error ? error.message : 'The labels could not be made.',
        dymo: error instanceof DymoPrintError,
      });
    } finally {
      setPrinting(false);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Labeling"
        description="Scan a product’s barcode, or paste an ASIN or a seller SKU, and press Enter. Each product is added under the last with one label; set the quantities, then print them all as one job."
      />

      <Card className="space-y-3 p-5">
        <form onSubmit={onSubmit} className="flex items-end gap-2">
          <label className="block min-w-0 flex-1">
            <span className="mb-1 block text-sm font-medium text-body">
              Barcode, ASIN, or seller SKU
            </span>
            <Input
              ref={inputRef}
              value={entry}
              onChange={(event) => setEntry(event.target.value)}
              autoFocus
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={100}
              placeholder="Scan or type, then Enter"
              className="font-mono"
            />
          </label>
          <Button type="submit" disabled={entry.trim() === ''}>
            Add
          </Button>
        </form>
        {pending > 0 && <p className="text-xs text-muted">Looking up…</p>}
        {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
        {choice && (
          <ChoicePanel choice={choice} onChoose={choose} onDismiss={() => setChoice(null)} />
        )}
        {/* Read out by a screen reader; the scanner's user is not looking at the box. */}
        <p className="sr-only" role="status" aria-live="polite">
          {announcement}
        </p>
      </Card>

      <Card className="p-5">
        {rows.length === 0 ? (
          <p className="text-sm text-muted">
            Nothing scanned yet. Scanned products are listed here.
          </p>
        ) : (
          <TableScroll>
            <table className="w-full text-sm" aria-label="Labels to print">
              <thead className="border-b border-line text-left text-xs text-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Product</th>
                  <th className="w-28 px-3 py-2 font-medium">Labels</th>
                  <th className="w-24 px-3 py-2 font-medium">
                    <span className="sr-only">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((row) => (
                  <QueueRowView
                    key={row.id}
                    row={row}
                    highlighted={highlight === row.id}
                    onQuantity={(qty) => commit(setQuantity(rowsRef.current, row.id, qty))}
                    onRemove={() => {
                      commit(removeRow(rowsRef.current, row.id));
                      setAnnouncement(`Removed ${productName(row.product)}.`);
                      focusInput();
                    }}
                    onDone={focusInput}
                  />
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Card>

      <Card className="space-y-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-80 space-y-1">
            <p className="text-sm text-body">
              Printing to <span className="font-medium text-strong">{destination.name}</span>{' '}
              <Link
                to={LABEL_SETTINGS_PATH}
                className="text-brand-600 underline underline-offset-2"
              >
                Change
              </Link>
            </p>
            <LabelPrintNote destination={destination} dymo={dymo} />
            {dymoFellBack && (
              <div
                className="space-y-2 rounded-md bg-warn-bg p-3 ring-1 ring-inset ring-warn-line"
                data-testid="dymo-fallback"
              >
                <p className="text-xs text-warn-text">
                  This browser is set to print to the DYMO LabelWriter, but it is not available:{' '}
                  {dymoAvailable.reason} Until it is, labels go to {destination.name}.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => void refreshDymoStatus()}
                  disabled={dymo.checking}
                >
                  {dymo.checking ? 'Checking…' : 'Check again'}
                </Button>
              </div>
            )}
            {dymoWaitingForPermission && (
              <div
                className="space-y-2 rounded-md bg-warn-bg p-3 text-xs text-warn-text ring-1 ring-inset ring-warn-line"
                data-testid="dymo-permission-wait"
              >
                {/* The status line just above says what the browser is waiting for. */}
                <p>
                  The check for the printer continues while the browser waits for your answer. To
                  print this list now, make a PDF instead:
                </p>
                <PdfInsteadLinks onPrint={(id) => void onPrint(id)} />
              </div>
            )}
            {/* Shown until the DYMO printer is the working destination: after that the setup is done. */}
            {!(dymoChosen && dymoAvailable.available) && <DymoConnectNote />}
          </div>
          {destination.supportsStartAt && (
            <label className="block w-40 shrink-0">
              <span className="mb-1 block text-sm font-medium text-body">Start at label</span>
              <Input
                value={startAtText}
                onChange={(event) => setStartAtText(event.target.value)}
                inputMode="numeric"
                aria-invalid={startAt === null}
                aria-describedby="start-at-hint"
                className={startAt === null ? 'ring-danger-line' : ''}
              />
              <span
                id="start-at-hint"
                className={`mt-1 block text-xs ${startAt === null ? 'text-danger-text' : 'text-muted'}`}
              >
                {startAt === null
                  ? `A whole number from 1 to ${perPage}.`
                  : `1 for a new sheet; up to ${perPage} on a partly used one.`}
              </span>
            </label>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
          <Button onClick={() => void onPrint()} disabled={!canPrint}>
            {printing
              ? destination.kind === 'dymo'
                ? 'Sending to the printer…'
                : 'Making labels…'
              : dymoPending
                ? 'Checking the printer…'
                : 'Print all labels'}
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={rows.length === 0}>
            Cancel
          </Button>
          <p className="text-sm text-muted" data-testid="label-totals">
            {totals.rows === 0
              ? 'No labels yet.'
              : `${plural(totals.labels, 'label')} for ${plural(totals.printableRows, 'product')}.`}
            {totals.skippedRows > 0 &&
              ` ${plural(totals.skippedRows, 'product')} with no FNSKU will be left out.`}
          </p>
        </div>

        {printError && (
          <Alert>
            <div className="space-y-2" data-testid="label-print-error">
              <p>{printError.message}</p>
              {printError.dymo && (
                <>
                  <p>The list is unchanged. To print it another way, make a PDF instead:</p>
                  <PdfInsteadLinks onPrint={(id) => void onPrint(id)} />
                </>
              )}
            </div>
          </Alert>
        )}
        {printed?.kind === 'dymo' && (
          <Alert tone="success">
            <div className="space-y-2" data-testid="label-print-result">
              <p>
                Sent {plural(printed.labels, 'label')} to {printed.printerName}
                {printed.jobs === 1 ? ' as one job' : ` as ${printed.jobs} jobs`}, with no print
                dialog.
                {printed.skippedRows > 0 &&
                  ` ${plural(printed.skippedRows, 'product')} with no FNSKU ${
                    printed.skippedRows === 1 ? 'was' : 'were'
                  } left out.`}
              </p>
              {printed.warnings.map((warning) => (
                <p key={warning} className="font-medium text-warn-text">
                  {warning}
                </p>
              ))}
              <p className="flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={clearList}
                  className="font-medium underline underline-offset-2"
                >
                  Clear list
                </button>
              </p>
              <p className="text-xs">
                The app is told the job was accepted, not that every label came out. If the roll ran
                out or jammed, the list is kept so you can print again. Clear it when the labels are
                on the products.
              </p>
            </div>
          </Alert>
        )}
        {printed?.kind === 'pdf' && printed.url !== null && (
          <Alert tone="success">
            <div className="space-y-2" data-testid="label-print-result">
              <p>
                Made one PDF: {plural(printed.labels, 'label')} on {plural(printed.pages, 'page')}{' '}
                for {printed.destination.name}.{' '}
                {printed.opened
                  ? 'It opened in a new tab; print it from there.'
                  : 'Your browser blocked the new tab; open it with the link below.'}
                {printed.skippedRows > 0 &&
                  ` ${plural(printed.skippedRows, 'product')} with no FNSKU ${
                    printed.skippedRows === 1 ? 'was' : 'were'
                  } left out.`}
                {printed.fellBack &&
                  ` The chosen printer was not available${
                    printed.fallbackReason ? ` (${printed.fallbackReason})` : ''
                  }, so this used the default.`}
              </p>
              <p className="flex flex-wrap items-center gap-4">
                <a
                  href={printed.url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium underline underline-offset-2"
                >
                  Open PDF
                </a>
                <a
                  href={printed.url}
                  download={printed.fileName}
                  className="font-medium underline underline-offset-2"
                >
                  Download PDF
                </a>
                <button
                  type="button"
                  onClick={clearList}
                  className="font-medium underline underline-offset-2"
                >
                  Clear list
                </button>
              </p>
              <p className="text-xs">
                The list is kept so you can print again after a jam. Clear it when the labels are on
                the products.
              </p>
            </div>
          </Alert>
        )}
      </Card>
    </div>
  );
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** The "start at label" box as a position on the sheet, or null when it is not one. */
function parseStartAt(text: string, perPage: number): number | null {
  const trimmed = text.trim();
  if (!/^[0-9]{1,3}$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= 1 && value <= perPage ? value : null;
}

/** The lines that identify a product: the title, the taxonomy, and its codes. */
function ProductLines({ product }: { product: LabelProduct }) {
  const name = productName(product);
  // A taxonomy that only repeats the title (a product resolved no further than
  // its model) adds nothing to confirm by.
  const resolved = taxonomyLine(product.taxonomy);
  const taxonomy = resolved.toLowerCase() === name.toLowerCase() ? '' : resolved;
  const matched = matchedByText(product.matchedBy);
  return (
    <div className="min-w-0">
      <p className="font-medium text-strong">{name}</p>
      {taxonomy && <p className="text-xs text-body">{taxonomy}</p>}
      <p className="mt-0.5 flex flex-wrap gap-x-3 font-mono text-xs text-muted">
        <span>SKU {product.sellerSku}</span>
        {product.asin && <span>ASIN {product.asin}</span>}
        <span>FNSKU {product.fnsku ?? '—'}</span>
        {product.status && product.status !== 'Active' && (
          <span className="font-sans text-warn-text">{product.status}</span>
        )}
      </p>
      {matched && <p className="text-xs text-faint">{matched}</p>}
      {product.note && <p className="mt-1 text-xs font-medium text-warn-text">{product.note}</p>}
      {product.otherSkus.length > 0 && (
        <p className="mt-1 text-xs text-muted">
          Also live on this ASIN:{' '}
          {product.otherSkus.map((s) => `${s.sellerSku} (${s.fnsku ?? 'no FNSKU'})`).join(', ')}.
          The label uses {product.sellerSku}.
        </p>
      )}
    </div>
  );
}

function ChoicePanel({
  choice,
  onChoose,
  onDismiss,
}: {
  choice: PendingChoice;
  onChoose: (product: LabelProduct) => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className="space-y-2 rounded-md bg-warn-bg p-3 ring-1 ring-inset ring-warn-line"
      role="group"
      aria-label="Choose a product"
    >
      <p className="text-sm text-warn-text">
        “{choice.query}” matches {choice.products.length} products. Choose the one in your hand;
        nothing is added until you do.
      </p>
      <ul className="space-y-2">
        {choice.products.map((product) => (
          <li
            key={product.key}
            className="flex items-center gap-3 rounded-md bg-surface p-2 ring-1 ring-inset ring-line"
          >
            <ProductThumb asin={product.asin} title={product.title} size={48} />
            <ProductLines product={product} />
            <Button
              variant="secondary"
              className="ml-auto shrink-0"
              onClick={() => onChoose(product)}
              aria-label={`Add ${productName(product)} (${product.sellerSku})`}
            >
              Add this one
            </Button>
          </li>
        ))}
      </ul>
      <Button variant="ghost" onClick={onDismiss}>
        None of these
      </Button>
    </div>
  );
}

function QueueRowView({
  row,
  highlighted,
  onQuantity,
  onRemove,
  onDone,
}: {
  row: QueueRow;
  highlighted: boolean;
  onQuantity: (qty: number) => void;
  onRemove: () => void;
  /** Enter in the quantity box: back to the scan box for the next product. */
  onDone: () => void;
}) {
  const { product } = row;
  // What is typed, which may not be a quantity yet; the row's own quantity only
  // ever holds a valid one.
  const [text, setText] = useState(String(row.qty));
  const [lastQty, setLastQty] = useState(row.qty);
  if (row.qty !== lastQty) {
    // The quantity changed from outside the box (a repeat scan): show it.
    setLastQty(row.qty);
    setText(String(row.qty));
  }
  const invalid = parseQuantity(text) === null;

  function onChange(value: string) {
    setText(value);
    const qty = parseQuantity(value);
    if (qty !== null) {
      setLastQty(qty);
      onQuantity(qty);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (invalid) setText(String(row.qty));
    onDone();
  }

  return (
    <tr
      data-row-id={row.id}
      data-highlighted={highlighted ? 'true' : undefined}
      className={`transition-colors duration-700 ${highlighted ? 'bg-brand-50' : ''}`}
    >
      <td className="px-3 py-2">
        <div className="flex items-center gap-3">
          <ProductThumb asin={product.asin} title={product.title} size={56} />
          <ProductLines product={product} />
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <Input
          value={text}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => invalid && setText(String(row.qty))}
          onKeyDown={onKeyDown}
          onFocus={(event) => event.target.select()}
          inputMode="numeric"
          maxLength={3}
          aria-label={`Labels for ${productName(product)}`}
          aria-invalid={invalid}
          className={`tabular text-right ${invalid ? 'ring-danger-line' : ''}`}
        />
        {invalid && (
          <p className="mt-1 text-xs text-danger-text">
            {MIN_QTY} to {MAX_QTY}
          </p>
        )}
      </td>
      <td className="px-3 py-2 text-right align-top">
        <Button variant="ghost" onClick={onRemove} aria-label={`Remove ${productName(product)}`}>
          Remove
        </Button>
      </td>
    </tr>
  );
}
